import axios from 'axios';

export const READING_HAS_PROTECTED_DATA = 'READING_HAS_PROTECTED_DATA';
export const READING_RECOVERY_STALE = 'READING_RECOVERY_STALE';

function decisionModal() {
    return window.decisionModalInstance?.$refs?.modal || null;
}

async function choose(message, choices, title) {
    const instance = decisionModal();
    if (!instance || typeof instance.choose !== 'function') return null;

    try {
        return await instance.choose(message, choices, title, 'warning', 'Скасувати');
    } catch (_) {
        return null;
    }
}

async function selectOne(message, choices, title, preselectedValue = null) {
    const instance = decisionModal();
    if (!instance || typeof instance.selectOne !== 'function') return null;

    try {
        return await instance.selectOne(
            message,
            choices,
            title,
            'warning',
            'Скасувати',
            'Продовжити',
            preselectedValue,
        );
    } catch (_) {
        return null;
    }
}

function notify(message, title = 'Захищене прочитання', type = 'warning') {
    window.alert(message, title, type);
}

export async function fetchReadingRecovery(readingInstanceId, intent = 'history_item_deletion') {
    const { data } = await axios.get(
        `/api/reading-instances/${readingInstanceId}/removal-recovery`,
        { params: { intent } },
    );

    return data?.recovery || null;
}

function expectedPayload(snapshot) {
    const expected = snapshot.expected || {};

    return {
        intent: snapshot.intent,
        expected_brs_id: expected.brs_id,
        expected_brs_lock_version: expected.brs_lock_version,
        expected_source_lock_version: expected.source_lock_version,
        expected_publication_id: expected.publication_id ?? null,
        expected_publication_updated_at: expected.publication_updated_at ?? null,
        expected_publication_rating: expected.publication_rating ?? null,
    };
}

async function confirmDestructiveAction(action, snapshot, transferTarget = null) {
    if (action === 'preserve_abandoned') return true;

    let message = 'Прочитання буде видалено без можливості відновлення.';
    if (action === 'preserve_text_as_draft') {
        const isOpinion = snapshot.publication?.type === 'opinion';
        const retainedPublication = isOpinion
            ? 'Відгук збережеться як приватна чернетка.'
            : 'Рецензія збережеться як приватна чернетка.';
        const hasRating = snapshot.publication?.rating !== null
            || snapshot.reading?.rating !== null;
        message = `${hasRating ? 'Читання й оцінку' : 'Читання'} буде видалено, а ${retainedPublication}`;
    } else if (action === 'delete_all') {
        message = [
            snapshot.actions?.find((item) => item.value === action)?.label || 'Видалити пов’язані дані',
            'Цю дію неможливо скасувати.',
        ].join('\n\n');
    } else if (action === 'transfer_publication') {
        const publication = snapshot.publication?.type === 'opinion' ? 'Відгук' : 'Рецензію';
        message = [
            `${publication} буде перенесено до:`,
            transferTarget?.display_label,
            'Поточне прочитання буде видалено.',
        ].filter(Boolean).join('\n\n');
    }

    return window.confirm(message, 'Підтвердьте дію', 'warning');
}

async function selectTransferTarget(snapshot) {
    const candidates = snapshot.transfer_candidates || [];
    const publication = snapshot.publication?.type === 'opinion' ? 'Відгук' : 'Рецензію';
    const targetId = await selectOne(
        'Оберіть конкретне прочитання. Нове прочитання в цьому кроці не створюється.',
        candidates.map((candidate) => ({
            value: candidate.id,
            label: candidate.display_label,
        })),
        `Куди перенести ${publication}?`,
        candidates.length === 1 ? candidates[0].id : null,
    );
    if (!targetId) return null;

    const target = candidates.find((candidate) => Number(candidate.id) === Number(targetId));
    if (!target) return null;

    let confirmRatingConflict = false;
    if (target.rating !== null
        && Number(target.rating) !== Number(snapshot.publication?.rating)) {
        const subject = snapshot.publication?.type === 'opinion' ? 'Відгук' : 'Рецензія';
        confirmRatingConflict = await window.confirm(
            [
                `Вибране прочитання вже має оцінку ${target.rating}/10.`,
                `${subject} змінить оцінку з ${snapshot.publication?.rating}/10 на ${target.rating}/10.`,
                'Поточне прочитання після цього буде видалено.',
            ].join('\n\n'),
            'Змінити оцінку публікації?',
            'warning',
        );
        if (!confirmRatingConflict) return null;
    }

    return { target, confirmRatingConflict };
}

export async function requestReadingRecovery(snapshot, intent = null, options = {}) {
    const instance = decisionModal();
    if (options.trigger && typeof instance?.setReturnFocusTarget === 'function') {
        instance.setReturnFocusTarget(options.trigger);
    }
    if (!snapshot) return { completed: false, cancelled: true };
    if (snapshot.integrity_error || !(snapshot.actions || []).length) {
        notify(snapshot.message || 'Видалення зупинено через неузгоджені пов’язані дані.');
        return { completed: false, blocked: true };
    }

    const simpleDelete = !snapshot.protected
        && snapshot.actions.length === 1
        && snapshot.actions[0].value === 'delete_all';
    const action = simpleDelete
        ? 'delete_all'
        : await choose(
            [snapshot.message, snapshot.reading?.display_label].filter(Boolean).join('\n\n'),
            snapshot.actions,
            'Що зробити з прочитанням?',
        );
    if (!action) return { completed: false, cancelled: true };

    let transferSelection = null;
    if (action === 'transfer_publication') {
        transferSelection = await selectTransferTarget(snapshot);
        if (!transferSelection) return { completed: false, cancelled: true };
    }

    const confirmed = await confirmDestructiveAction(action, snapshot, transferSelection?.target);
    if (!confirmed) return { completed: false, cancelled: true };

    const payload = {
        ...expectedPayload(snapshot),
        action,
        intent: intent || snapshot.intent,
        ...(transferSelection ? {
            target_reading_instance_id: transferSelection.target.id,
            expected_target_lock_version: transferSelection.target.lock_version,
            expected_target_rating: transferSelection.target.rating ?? null,
            confirm_rating_conflict: transferSelection.confirmRatingConflict,
        } : {}),
    };

    try {
        const response = await axios.post(snapshot.endpoint, payload);
        const readingStatus = response?.data?.reading_status || {};

        return {
            completed: true,
            removed: readingStatus.status === null,
            status: readingStatus.status ?? null,
            response,
            action,
        };
    } catch (error) {
        if (error?.response?.status === 409
            && error?.response?.data?.code === READING_RECOVERY_STALE) {
            return {
                completed: false,
                stale: true,
                error,
                recovery: error.response.data.recovery || null,
                readingStatus: error.response.data.reading_status || null,
            };
        }
        throw error;
    }
}
